import { useState, useEffect, useCallback } from 'react';
import {
  getCompanyRewards,
  getAvailableRewards,
  getPublicAvailableRewards,
  createReward,
  updateReward,
  deleteReward,
  requestRedemption,
  getCustomerRedemptions,
  getCompanyRedemptions,
  updateRedemptionStatus,
  getRedemptionDetails
} from '../services/rewardsService';

export const useRewards = (companyId = null) => {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Buscar prêmios da empresa
  const fetchRewards = useCallback(async (activeOnly = true) => {
    if (!companyId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await getCompanyRewards(companyId, activeOnly);
      setRewards(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Criar novo prêmio
  const createNewReward = async (rewardData) => {
    setLoading(true);
    setError(null);
    
    try {
      const newReward = await createReward(companyId, rewardData);
      setRewards(prev => [...prev, newReward]);
      return newReward;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Atualizar prêmio
  const updateExistingReward = async (rewardId, updateData) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedReward = await updateReward(rewardId, updateData);
      setRewards(prev => 
        prev.map(reward => 
          reward.id === rewardId ? { ...reward, ...updatedReward } : reward
        )
      );
      return updatedReward;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Deletar prêmio
  const removeReward = async (rewardId) => {
    setLoading(true);
    setError(null);
    
    try {
      await deleteReward(rewardId);
      setRewards(prev => prev.filter(reward => reward.id !== rewardId));
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Buscar prêmios disponíveis para um cliente (autenticado)
  const getCustomerAvailableRewards = async (customerPoints) => {
    if (!companyId) return [];
    
    try {
      return await getAvailableRewards(companyId, customerPoints);
    } catch (err) {
      setError(err.message);
      return [];
    }
  };

  // Buscar prêmios disponíveis para um cliente (público)
  const getPublicCustomerAvailableRewards = async (customerPoints) => {
    if (!companyId) return [];
    
    try {
      return await getPublicAvailableRewards(companyId, customerPoints);
    } catch (err) {
      console.error('Erro ao buscar prêmios públicos:', err);
      return [];
    }
  };

  useEffect(() => {
    if (companyId) {
      fetchRewards();
    }
  }, [fetchRewards, companyId]);

  return {
    rewards,
    loading,
    error,
    fetchRewards,
    createNewReward,
    updateExistingReward,
    removeReward,
    getCustomerAvailableRewards,
    getPublicCustomerAvailableRewards
  };
};

export const useRedemptions = () => {
  const [redemptions, setRedemptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Solicitar resgate
  const requestNewRedemption = async (customerId, companyId, rewardId, customerPoints) => {
    setLoading(true);
    setError(null);
    
    try {
      const redemption = await requestRedemption(customerId, companyId, rewardId, customerPoints);
      return redemption;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Buscar resgates do cliente
  const fetchCustomerRedemptions = async (customerId) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getCustomerRedemptions(customerId);
      setRedemptions(data);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Buscar resgates da empresa
  const fetchCompanyRedemptions = async (companyId, status = null) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getCompanyRedemptions(companyId, status);
      setRedemptions(data);
      return data;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Atualizar status do resgate
  const updateStatus = async (redemptionId, status, notes = '', additionalData = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const updatedRedemption = await updateRedemptionStatus(redemptionId, status, notes, additionalData);
      setRedemptions(prev => 
        prev.map(redemption => 
          redemption.id === redemptionId 
            ? { ...redemption, ...updatedRedemption } 
            : redemption
        )
      );
      return updatedRedemption;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Buscar detalhes do resgate
  const fetchRedemptionDetails = async (redemptionId) => {
    setLoading(true);
    setError(null);
    
    try {
      const details = await getRedemptionDetails(redemptionId);
      return details;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    redemptions,
    loading,
    error,
    requestNewRedemption,
    fetchCustomerRedemptions,
    fetchCompanyRedemptions,
    updateStatus,
    fetchRedemptionDetails
  };
};
